import React, { useEffect, useState } from "react";
import {
  ClockIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { apiService } from "../../services/api";
import { SubscriptionStatus } from "../../types/api";

interface UsageDisplayProps {
  secondsUsed: number;
  secondsLimit: number;
  periodEnd: string;
  compact?: boolean;
}

const UsageDisplay: React.FC<UsageDisplayProps> = ({
  secondsUsed,
  secondsLimit,
  periodEnd,
  compact = false,
}) => {
  const [subscriptionStatus, setSubscriptionStatus] =
    useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const response = await apiService.getSubscriptionStatus();
        if (response.data) {
          setSubscriptionStatus(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch subscription status:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSubscription();
  }, []);

  const minutesUsed = Math.floor(secondsUsed / 60);
  const minutesLimit = Math.floor(secondsLimit / 60);
  const percentage = (secondsUsed / secondsLimit) * 100;

  const now = new Date();
  const gracePeriodDate = subscriptionStatus?.gracePeriodEnds
    ? new Date(subscriptionStatus.gracePeriodEnds)
    : null;
  const isInGracePeriod = gracePeriodDate && now <= gracePeriodDate;
  const gracePeriodExpired = gracePeriodDate && now > gracePeriodDate;

  const getStatusColor = () => {
    if (subscriptionStatus?.status === "canceled" || gracePeriodExpired) {
      return "text-red-600 bg-red-50 border-red-200";
    }
    if (subscriptionStatus?.status === "past_due") {
      return "text-orange-600 bg-orange-50 border-orange-200";
    }
    if (percentage >= 100) return "text-red-600 bg-red-50 border-red-200";
    if (percentage >= 90)
      return "text-orange-600 bg-orange-50 border-orange-200";
    if (percentage >= 80)
      return "text-yellow-600 bg-yellow-50 border-yellow-200";
    return "text-green-600 bg-green-50 border-green-200";
  };

  const getProgressBarColor = () => {
    if (subscriptionStatus?.status === "canceled" || gracePeriodExpired)
      return "bg-red-600";
    if (subscriptionStatus?.status === "past_due") return "bg-orange-500";
    if (percentage >= 100) return "bg-red-600";
    if (percentage >= 90) return "bg-orange-500";
    if (percentage >= 80) return "bg-yellow-500";
    return "bg-green-500";
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  if (loading) {
    return compact ? (
      <div className="inline-flex items-center px-3 py-1.5 rounded-md border border-gray-200">
        <ClockIcon className="h-4 w-4 mr-2 text-gray-400 animate-pulse" />
        <span className="text-sm text-gray-400">Loading...</span>
      </div>
    ) : (
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="animate-pulse space-y-2">
          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
          <div className="h-8 bg-gray-200 rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <div
        className={`inline-flex items-center px-3 py-1.5 rounded-md border ${getStatusColor()}`}
      >
        <ClockIcon className="h-4 w-4 mr-2" />
        <span className="text-sm font-medium">
          {minutesUsed.toLocaleString()} / {minutesLimit.toLocaleString()} min
        </span>
        {(percentage >= 90 ||
          subscriptionStatus?.status === "past_due" ||
          subscriptionStatus?.status === "canceled") && (
          <ExclamationTriangleIcon className="h-4 w-4 ml-2" />
        )}
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <ClockIcon className="h-5 w-5 text-gray-400" />
          <h3 className="text-sm font-medium text-gray-900">Monthly Usage</h3>
        </div>
        {(percentage >= 90 ||
          subscriptionStatus?.status === "past_due" ||
          subscriptionStatus?.status === "canceled") && (
          <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />
        )}
      </div>

      {/* Payment Failed Banners */}
      {subscriptionStatus?.status === "canceled" && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
          <div className="flex items-start">
            <XCircleIcon className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="block mb-1">Subscription Canceled</strong>
              <p className="text-xs">
                Your subscription has been canceled. Uploads are disabled.
                Please reactivate your subscription.
              </p>
            </div>
          </div>
        </div>
      )}

      {subscriptionStatus?.status === "past_due" && gracePeriodExpired && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
          <div className="flex items-start">
            <XCircleIcon className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="block mb-1">
                Payment Failed - Grace Period Expired
              </strong>
              <p className="text-xs mb-2">
                Your grace period has ended. Uploads are now disabled until
                payment is received.
              </p>
              <a
                href="/settings/billing"
                className="text-xs font-medium text-red-700 underline hover:text-red-900"
              >
                Update Payment Method →
              </a>
            </div>
          </div>
        </div>
      )}

      {subscriptionStatus?.status === "past_due" &&
        isInGracePeriod &&
        subscriptionStatus.gracePeriodEnds && (
          <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded text-sm text-orange-800">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="h-5 w-5 text-orange-600 mr-2 flex-shrink-0 mt-0.5" />
              <div>
                <strong className="block mb-1">
                  Payment Failed - Grace Period Active
                </strong>
                <p className="text-xs mb-1">
                  Your payment could not be processed. You can continue
                  uploading until{" "}
                  <strong>
                    {formatDateTime(subscriptionStatus.gracePeriodEnds)}
                  </strong>
                  .
                </p>
                <p className="text-xs mb-2">
                  After this time, uploads will be blocked until payment is
                  received.
                </p>
                <a
                  href="/settings/billing"
                  className="text-xs font-medium text-orange-700 underline hover:text-orange-900"
                >
                  Update Payment Method Now →
                </a>
              </div>
            </div>
          </div>
        )}

      {/* Usage Stats */}
      <div className="space-y-2">
        <div className="flex justify-between items-baseline">
          <span className="text-2xl font-bold text-gray-900">
            {minutesUsed.toLocaleString()}
          </span>
          <span className="text-sm text-gray-500">
            of {minutesLimit.toLocaleString()} minutes
          </span>
        </div>

        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-300 ${getProgressBarColor()}`}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>

        <div className="flex justify-between text-xs text-gray-500">
          <span>{percentage.toFixed(1)}% used</span>
          <span>Resets {formatDate(periodEnd)}</span>
        </div>

        {/* Usage warnings */}
        {percentage >= 100 && subscriptionStatus?.status === "active" && (
          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800">
            <strong>Limit Reached:</strong> You've reached your monthly limit.
            New uploads will be rejected.
          </div>
        )}
        {percentage >= 90 &&
          percentage < 100 &&
          subscriptionStatus?.status === "active" && (
            <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded text-xs text-orange-800">
              <strong>Warning:</strong> You're approaching your monthly limit.
            </div>
          )}
      </div>
    </div>
  );
};

export default UsageDisplay;
